import { BarList, DeviationBars, TrendChart } from '@/shared/components/charts'
import { EmptyState, ErrorState, SkeletonCards } from '@/shared/components/data'
import { useI18n, useT } from '@/shared/i18n'

import { useMetricsInsights } from '../api'

/**
 * Las tendencias: lo que un número de hoy no puede contestar.
 *
 * Los cuatro gráficos están ordenados por la pregunta que responden, no por
 * cómo se ven:
 *
 * 1. **¿Se acumula trabajo?** Lo creado contra lo cerrado.
 * 2. **¿Cuánta deuda arrastramos?** Vencidas al cierre de cada semana. Ésta es
 *    la que no se puede ver de ninguna otra forma en la app: el portafolio dice
 *    cuántas hay hoy, y "quince" puede ser una mala semana o un techo que sube
 *    hace dos meses. Son dos problemas distintos.
 * 3. **¿Llegamos a lo que prometemos?** Cumplimiento semanal.
 * 4. **¿Dónde tardamos más?** Tiempo de ciclo por empresa, y el desvío de cada
 *    entrega contra su fecha objetivo.
 *
 * Carga **aparte** del tablero de arriba: barre doce semanas de historia, y la
 * cabecera —que es lo primero que se mira— no tiene por qué esperarla.
 */
export function TrendsSection() {
  const t = useT()
  const { formatDate, locale } = useI18n()
  const query = useMetricsInsights()

  if (query.isPending) return <SkeletonCards count={2} />
  if (query.isError) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />
  }

  const {
    weekly,
    cycle_time: ciclos,
    deliveries: entregas,
    weeks,
    window_days: ventana,
  } = query.data

  // Sin una sola tarea creada ni cerrada en doce semanas no hay tendencia que
  // dibujar: cuatro gráficos planos en cero ocupan media pantalla para decir
  // que no hay nada, y lo dicen peor que una frase.
  const hayHistoria = weekly.some((p) => p.created > 0 || p.closed > 0 || p.overdue_at_end > 0)
  if (!hayHistoria) {
    return (
      <EmptyState
        title={t('metrics.trends.empty.title')}
        description={t('metrics.trends.empty.description')}
      />
    )
  }

  // Etiquetas cortas: en doce semanas el año no aporta y el día sí. El orden
  // de día y mes lo pone el idioma —no es 22/09 en todos lados— y la zona va
  // fija en UTC porque `week_start` es una fecha sin hora: interpretarla en la
  // zona local la corre un día hacia atrás al oeste de Greenwich, y la semana
  // entera queda etiquetada con el domingo anterior.
  const etiquetaCorta = new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    timeZone: 'UTC',
  })
  const etiquetas = weekly.map((p) => etiquetaCorta.format(new Date(`${p.week_start}T00:00:00Z`)))

  return (
    <div className="flex flex-col gap-3">
      {/* Que el gráfico se pueda leer punto por punto no se adivina: sin esta
          línea, el hover queda descubierto sólo por accidente. */}
      <p className="text-2xs text-text-3">{t('metrics.trends.hint', { weeks })}</p>

      <div className="grid items-start gap-5 lg:grid-cols-2">
        <Panel title={t('metrics.trends.flow.title')} hint={t('metrics.trends.flow.hint')}>
          <TrendChart
            etiquetas={etiquetas}
            resumenAccesible={t('metrics.trends.flow.summary', { weeks })}
            series={[
              {
                label: t('metrics.trends.flow.created'),
                valores: weekly.map((p) => p.created),
                rol: 'contexto',
              },
              {
                label: t('metrics.trends.flow.closed'),
                valores: weekly.map((p) => p.closed),
                rol: 'serie',
              },
            ]}
          />
        </Panel>

        <Panel title={t('metrics.trends.debt.title')} hint={t('metrics.trends.debt.hint')}>
          <TrendChart
            etiquetas={etiquetas}
            resumenAccesible={t('metrics.trends.debt.summary', { weeks })}
            series={[
              {
                label: t('metrics.trends.debt.series'),
                valores: weekly.map((p) => p.overdue_at_end),
                rol: 'alerta',
                conArea: true,
              },
            ]}
          />
        </Panel>

        <Panel title={t('metrics.trends.onTime.title')} hint={t('metrics.trends.onTime.hint')}>
          <TrendChart
            etiquetas={etiquetas}
            topeFijo={100}
            resumenAccesible={t('metrics.trends.onTime.summary')}
            series={[
              {
                label: t('metrics.trends.onTime.series'),
                // `null` viaja tal cual: la línea se corta en las semanas sin
                // nada que medir en vez de caer a cero.
                valores: weekly.map((p) => p.on_time_rate),
                rol: 'serie',
                sufijo: '%',
              },
            ]}
          />
        </Panel>

        <Panel
          title={t('metrics.trends.cycle.title')}
          hint={t('metrics.trends.cycle.hint', { days: ventana })}
        >
          {ciclos.length === 0 ? (
            <p className="text-xs text-text-3">{t('common.state.nothingHere')}</p>
          ) : (
            <BarList
              resumenAccesible={t('metrics.trends.cycle.summary')}
              items={ciclos.map((c) => ({
                id: c.company_id,
                label: c.company_name,
                hue: c.company_color_hue,
                valor: c.avg_days,
                texto: t('metrics.trends.cycle.days', { count: c.avg_days }),
                detalle: t('metrics.trends.cycle.sample', { count: c.closed }),
              }))}
            />
          )}
        </Panel>
      </div>

      <Panel
        title={t('metrics.trends.delivery.title')}
        hint={t('metrics.trends.delivery.hint', { days: ventana })}
      >
        {entregas.length === 0 ? (
          <p className="text-xs text-text-3">{t('common.state.nothingHere')}</p>
        ) : (
          <DeviationBars
            antes={t('metrics.trends.delivery.early')}
            despues={t('metrics.trends.delivery.late')}
            sinObjetivo={t('metrics.trends.delivery.noTarget')}
            resumenAccesible={t('metrics.trends.delivery.summary')}
            items={entregas.map((d) => ({
              id: d.project_id,
              label: d.project_name,
              detalle: `${d.company_name} · ${formatDate(d.actual_end_date)}`,
              dias: d.deviation_days,
            }))}
          />
        )}
      </Panel>
    </div>
  )
}

/** Marco común: hairline y superficie, como el resto de la app. */
function Panel({
  title,
  hint,
  children,
}: {
  title: string
  hint: string
  children: React.ReactNode
}) {
  return (
    <section className="hairline flex flex-col gap-3 rounded-[var(--radius-card)] bg-surface-1 p-3">
      <header className="flex flex-col gap-0.5">
        <h3 className="text-sm text-text-1">{title}</h3>
        <p className="text-2xs text-text-3">{hint}</p>
      </header>
      {children}
    </section>
  )
}
